import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAuthSessionValid } from '../utils/auth';
import { getRoleById } from '../firebase/services/roleService';

type ProtectedLaunchRampRouteProps = {
  children: React.ReactNode;
};

const ProtectedLaunchRampRoute: React.FC<ProtectedLaunchRampRouteProps> = ({ children }) => {
  const { currentUser, userProfile, loading, isPasscodeAuthenticated } = useAuth();
  const location = useLocation();
  const [roleName, setRoleName] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState<boolean>(true);
  const [validAuthSession, setValidAuthSession] = useState<boolean | null>(null);
  const [authChecking, setAuthChecking] = useState<boolean>(true);
  
  // Check if auth session is valid
  useEffect(() => {
    let mounted = true;
    const checkAuthSession = async () => {
      try {
        setAuthChecking(true);
        const valid = await isAuthSessionValid();
        if (!mounted) return;
        
        setValidAuthSession(valid);
        
        // Add detailed logging
        console.log('ProtectedLaunchRampRoute auth check:', {
          path: location.pathname,
          validAuthSession: valid,
          sessionExpired: valid ? 'No' : 'Yes',
          hasCurrentUser: !!currentUser,
          isPasscodeAuthenticated,
          authSessionData: localStorage.getItem('authSession')
        });
      } catch (error) {
        console.error('Error checking auth session:', error);
        if (!mounted) setValidAuthSession(false);
      } finally {
        if (mounted) setAuthChecking(false);
      }
    };
    
    checkAuthSession();
    return () => { mounted = false; };
  }, [location.pathname, currentUser, isPasscodeAuthenticated]);
  
  // Fetch role information
  useEffect(() => {
    let mounted = true;
    const fetchRole = async () => {
      try {
        if (userProfile?.roleId) {
          setRoleLoading(true);
          const roleDoc = await getRoleById(userProfile.roleId);
          if (!mounted) return;
          setRoleName(roleDoc?.name || null);
        } else {
          if (!mounted) return;
          setRoleName(null);
        }
      } catch (error) {
        console.error('Error fetching role:', error);
        if (!mounted) setRoleName(null);
      } finally {
        if (!mounted) return;
        setRoleLoading(false);
      }
    };
    
    fetchRole();
    return () => { mounted = false; };
  }, [userProfile]);
  
  // Show loading state while checking authentication
  if (loading || roleLoading || authChecking || validAuthSession === null) {
    console.log('ProtectedLaunchRampRoute loading state:', {
      loading,
      roleLoading,
      authChecking,
      validAuthSession
    });
    return <div>Loading...</div>;
  }
  
  // Check for valid authentication and role
  const hasValidRole = roleName && ['staff', 'admin'].some(allowedRole => 
    roleName.toLowerCase() === allowedRole.toLowerCase()
  );
  const isAuthenticated = validAuthSession === true || isPasscodeAuthenticated;
  
  // If authenticated and has valid role, allow access
  if (isAuthenticated && hasValidRole) {
    console.log('Access granted:', {
      viaSession: validAuthSession === true,
      viaPasscode: isPasscodeAuthenticated,
      role: roleName
    });
    return <>{children}</>;
  }
  
  // Otherwise, redirect to auth page
  console.log('Access denied, redirecting to auth page:', {
    isAuthenticated,
    hasValidRole,
    roleName,
    validAuthSession,
    isPasscodeAuthenticated
  });
  return <Navigate to="/auth" state={{ from: location.pathname }} />;
};

export default ProtectedLaunchRampRoute; 